/* Settings-bar controls for the 2D contour view.
 *
 * Deliberately parallel to the surface's rather than shared with it: the two have the same
 * gridding vocabulary but different appearance switches, and a factory taking both sets of flags
 * would be longer than the two files and read worse than either. What is shared is the settings
 * type's field names, so a change to the gridding contract still fails on both at compile time. */

import { ViewIds } from '@ardoq/api-types';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import { SettingsType, type SettingsConfig } from '@ardoq/view-settings';
import type {
  CoincidentAggregation,
  ContourViewSettings,
  ZInterpolation,
} from '@ardoq/xyz-charts';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import {
  isAggregationOffered,
  toListAggregation,
} from './sourceData/listAggregation';

const VIEW_ID = ViewIds.CONTOUR;

type AxisField = { name: string; label: string };

type FieldDimension = {
  key: 'selectedFieldNameX' | 'selectedFieldNameY' | 'selectedFieldNameZ';
  id: string;
  iconName: IconName;
  i18nKey: string;
  defaultLabel: string;
};

const FIELD_DIMENSIONS: FieldDimension[] = [
  {
    key: 'selectedFieldNameX',
    id: 'contourFieldX',
    iconName: IconName.ARROW_FORWARD,
    i18nKey: 'views:contour.menu.xField',
    defaultLabel: 'X axis field',
  },
  {
    key: 'selectedFieldNameY',
    id: 'contourFieldY',
    iconName: IconName.ARROW_UPWARD,
    i18nKey: 'views:contour.menu.yField',
    defaultLabel: 'Y axis field',
  },
  {
    key: 'selectedFieldNameZ',
    id: 'contourFieldZ',
    iconName: IconName.PALETTE,
    /* "Value" rather than "Height": nothing here is raised, and calling it height would send a
       reader looking for a third axis that does not exist. */
    i18nKey: 'views:contour.menu.zField',
    defaultLabel: 'Value field',
  },
];

const INTERPOLATION_OPTIONS: [ZInterpolation, string, string][] = [
  [
    'linear',
    'Between components (linear)',
    'views:contour.interpolation.linear',
  ],
  ['smooth', 'Smoothed across the plane', 'views:contour.interpolation.smooth'],
  ['binned', 'Only where components are', 'views:contour.interpolation.binned'],
];

const AGGREGATION_OPTIONS: [CoincidentAggregation, string, string][] = [
  ['mean', 'Average', 'views:contour.aggregation.mean'],
  ['sum', 'Sum', 'views:contour.aggregation.sum'],
  ['max', 'Highest', 'views:contour.aggregation.max'],
  ['min', 'Lowest', 'views:contour.aggregation.min'],
];

const RESOLUTION_OPTIONS: [number, string, string][] = [
  [24, 'Coarse', 'views:contour.resolution.coarse'],
  [64, 'Medium', 'views:contour.resolution.medium'],
  [128, 'Fine', 'views:contour.resolution.fine'],
];

const fieldPicker = (
  { key, id, iconName, i18nKey, defaultLabel }: FieldDimension,
  viewSettings: ContourViewSettings,
  axisFields: AxisField[],
  i18nLocale: I18nLocale
): SettingsConfig => {
  const selected = viewSettings[key];
  const label = t(i18nLocale, i18nKey, defaultLabel);

  return {
    id,
    type: SettingsType.DROPDOWN,
    iconName,
    label,
    buttonText:
      axisFields.find(({ name }) => name === selected)?.label ?? label,
    options: axisFields.map(({ name, label: fieldLabel }) => ({
      name: `${id}_${name}`,
      label: fieldLabel,
      type: DropdownOptionType.OPTION,
      isActive: selected === name,
      onClick: () => handleStoreSetting(VIEW_ID, { [key]: name }),
    })),
    dropdownSize: DropdownSize.S,
    isDisabled: axisFields.length === 0,
  };
};

const interpolationDropdown = (
  viewSettings: ContourViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'contourInterpolation',
  type: SettingsType.DROPDOWN,
  iconName: IconName.LINE_CHART,
  label: t(
    i18nLocale,
    'views:contour.menu.interpolation',
    'Value between components'
  ),
  options: INTERPOLATION_OPTIONS.map(([interpolation, label, i18nKey]) => ({
    name: `contour_interpolation_${interpolation}`,
    label: t(i18nLocale, i18nKey, label),
    type: DropdownOptionType.OPTION,
    isActive: viewSettings.interpolation === interpolation,
    onClick: () => handleStoreSetting(VIEW_ID, { interpolation }),
  })),
  dropdownSize: DropdownSize.S,
});

const aggregationDropdown = (
  isListHeight: boolean,
  viewSettings: ContourViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'contourAggregation',
  type: SettingsType.DROPDOWN,
  iconName: IconName.CALCULATOR,
  label: t(
    i18nLocale,
    'views:contour.menu.aggregation',
    'Components on the same position'
  ),
  options: AGGREGATION_OPTIONS.filter(([aggregation]) =>
    isAggregationOffered(aggregation, isListHeight)
  ).map(([aggregation, label, i18nKey]) => ({
    name: `contour_aggregation_${aggregation}`,
    label: t(i18nLocale, i18nKey, label),
    type: DropdownOptionType.OPTION,
    isActive:
      toListAggregation(viewSettings.aggregation, isListHeight) === aggregation,
    onClick: () => handleStoreSetting(VIEW_ID, { aggregation }),
  })),
  dropdownSize: DropdownSize.S,
});

const resolutionDropdown = (
  viewSettings: ContourViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'contourResolution',
  type: SettingsType.DROPDOWN,
  iconName: IconName.GRID_ON,
  label: t(i18nLocale, 'views:contour.menu.resolution', 'Detail'),
  options: RESOLUTION_OPTIONS.map(([resolution, label, i18nKey]) => ({
    name: `contour_resolution_${resolution}`,
    label: t(i18nLocale, i18nKey, label),
    type: DropdownOptionType.OPTION,
    isActive: viewSettings.resolution === resolution,
    onClick: () => handleStoreSetting(VIEW_ID, { resolution }),
  })),
  dropdownSize: DropdownSize.S,
});

const appearanceDropdown = (
  viewSettings: ContourViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'contourAppearance',
  type: SettingsType.DROPDOWN,
  iconName: IconName.VISIBILITY,
  label: t(i18nLocale, 'views:contour.menu.appearance', 'Appearance'),
  options: [
    {
      name: 'contour_bands',
      label: t(i18nLocale, 'views:contour.appearance.bands', 'Filled bands'),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.showFilledBands,
      onClick: () =>
        handleStoreSetting(VIEW_ID, {
          showFilledBands: !viewSettings.showFilledBands,
        }),
    },
    {
      name: 'contour_isolines',
      label: t(
        i18nLocale,
        'views:contour.appearance.isolines',
        'Contour lines'
      ),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.showIsolines,
      onClick: () =>
        handleStoreSetting(VIEW_ID, {
          showIsolines: !viewSettings.showIsolines,
        }),
    },
    {
      name: 'contour_samples',
      label: t(
        i18nLocale,
        'views:contour.appearance.samplePositions',
        'Component positions'
      ),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.showSamplePositions,
      onClick: () =>
        handleStoreSetting(VIEW_ID, {
          showSamplePositions: !viewSettings.showSamplePositions,
        }),
    },
  ],
  dropdownSize: DropdownSize.S,
});

export type ContourViewModifiersArgs = {
  viewSettings: ContourViewSettings;
  axisFields: AxisField[];
  /** Whether height plots a list field, which offers no sum. */
  isListHeight: boolean;
  i18nLocale: I18nLocale;
};

/** The controls the settings bar shows. */
export const getContourViewModifiers = ({
  viewSettings,
  axisFields,
  isListHeight,
  i18nLocale,
}: ContourViewModifiersArgs): SettingsConfig[] => {
  return [
    ...FIELD_DIMENSIONS.map(dimension =>
      fieldPicker(dimension, viewSettings, axisFields, i18nLocale)
    ),
    interpolationDropdown(viewSettings, i18nLocale),
    aggregationDropdown(isListHeight, viewSettings, i18nLocale),
    resolutionDropdown(viewSettings, i18nLocale),
    appearanceDropdown(viewSettings, i18nLocale),
  ];
};
